<template>
  <zm-dialog
    class="opc-zod"
    title=""
    :visible.sync="visible"
    width="30%"
    append-to-body
    show-close
    @closed="handleCancel"
    :class="{
      'opc-modal': $context.is.modal,
    }"
  >
    <div class="opc-zod__head">
      <h2>{{ $t('billing.delta.cur_plan_details') }}</h2>
      <span>{{ plansCount }} {{ $t('billing.order_summary.item_ss') }}</span>
    </div>
    <PlanDetail ref="planDetail" />
    <div class="opc-zod__footer">
      <current-charge :modifier="currentChargeAmountModifier" />
    </div>
  </zm-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import Cart from '@/plans/mx/shopping-cart';
import CurrentCharge from '@/views/config/shopping-cart/current.vue';
import { multipleCapacityPlanTypes, planTypes, trackingPlanTypes } from '@/common/constants';
import { CART_DISPLAY_MODE } from '@/plans/mx/shopping-cart.vue';
import { isEmptyVal } from '@/utils';
import { dateTimeFormat } from '@/utils/dateformat';
import { calculateDiscountWithDiscountType } from '@/views/config/coupon/helper/utils';
import _ from 'lodash';
import { ConfirmComponent } from '@/components/common/service-dialog/confirm';
import { getViewCurrentPlanName } from '@/components/view-current-dialog/helper';

const PlanDetail = {
  mixins: [Cart],
  computed: {
    ...mapGetters(['boughtPlans', 'discountWithAmountMap', 'getSelectedPlans']),
    currentSelectedPlanTypeMapInfo() {
      return _.groupBy(this.getSelectedPlans, 'type');
    },
    currentSelectedPlanTypeList() {
      return _.keys(this.currentSelectedPlanTypeMapInfo);
    },
    mimic() {
      const planTypeList = this.currentSelectedPlanTypeList;
      const planTypeInfo = this.currentSelectedPlanTypeMapInfo;
      const plans = _.filter(this.boughtPlans, (x) => {
        const planType = x?.type;
        const category = x?.category;

        if (!_.includes(planTypeList, planType)) return false;

        // deal with multiple capacity plan list
        if (_.includes(multipleCapacityPlanTypes, planType)) {
          return x?.capacity === _.get(planTypeInfo, [planType, 0, 'capacity']);
        }

        //biz plus only should show current edit one, distinguish by category
        if (planType === planTypes.ZOOM_ONE_BUNDLE) {
          const categories = _.keys(_.groupBy(this.getSelectedPlans, "category"));
          if (!_.includes(categories, category)) {
            return false;
          }
        }

        return true;
      });
      const mode = CART_DISPLAY_MODE.BOUGHT;

      return {
        mode,
        plans,
      };
    },
    mode() {
      return this.mimic.mode;
    },
  },
  methods: {
    compute_delta_discount(plan) {
      const basicInfo = { discount: 0, expireDate: '' };
      if (isEmptyVal(plan)) return basicInfo;

      const { discountPlans } = this.discountWithAmountMap;
      const planCode = plan?.plan_code;
      const discountPlanInfo = discountPlans?.[planCode];
      if (isEmptyVal(discountPlanInfo)) return basicInfo;

      const boughtQuantity = plan?.cartInfo?.selected_number;
      const expireDate = discountPlanInfo?.expireDate ?? '';
      const discount = calculateDiscountWithDiscountType({
        discountType: discountPlanInfo?.couponDiscountType,
        discount: discountPlanInfo?.discount,
        amount: plan?.amount * boughtQuantity,
        quantity: boughtQuantity,
      });

      return {
        discount,
        expireDate,
      };
    },
    mx_delta_discount_view(plan) {
      const { expireDate, discount } = this.compute_delta_discount(plan);
      if (!discount) return null;

      const expiresDesc = expireDate ?
        ` (${this.$t('billing.coupon.expiresDesc', [dateTimeFormat(expireDate)])})` : '';
      const cartDiscountLabel = `${this.$t('billing.coupon.discount')} ${expiresDesc}`;

      return (
        <div class="opc-cart-item__discount __PLACEHOLDER_discount_title__">
          <span>{cartDiscountLabel}</span>
          <span class="opc-cart-item__value text-no-capitalize">{this.$money(discount)}</span>
        </div>
      );
    },
    renderTitle(plan) {
      const planName = getViewCurrentPlanName(plan);

      return (
        <div>
          {this.mx_title(
            planName,
            this.$money(this.mx_price$(plan)),
            plan?.plan_code,
            undefined,
          )}
        </div>
      );
    },
    mx_license(num, unit = this.$t('billing.planform.unit.license')) {
      return (
        <div class="opc-cart-item__text">
          {num} {unit}
        </div>
      );
    },
    $renderPrice(plans) {
      return (plans || this.mx_plans).map((plan) => {
        return (
          <div class="opc-cart-item">
            {this.renderTitle(plan)}
            {this.mx_delta_discount_view(plan)}
            {this.mx_license$(plan)}
            {this.mx_expire_date(this.expiredDate(plan)) || this.mx_cycle(plan.billing_cycle)}
          </div>
        );
      });
    },
    expiredDate() {
      return '';
    },
  },
  render() {
    return <div>{this.$renderPrice(this.mimic.plans)}</div>;
  },
};

export default {
  mixins: [ConfirmComponent],
  data() {
    return {
      planType: '',
    };
  },
  computed: {
    ...mapGetters(['boughtPlans', 'getSelectedPlans', 'getSelectedPlansAmount']),
    plansCount() {
      return this.getSelectedPlans?.length || 1;
    },
    trackingName() {
      return _.get(trackingPlanTypes, _.get(planTypes, [this.planType]));
    },
  },
  components: {
    PlanDetail,
    CurrentCharge,
  },
  methods: {
    handleCancel() {
      this.$tracking.buyInteraction('product details', `${this.trackingName} plan details - exit`);
    },
    currentChargeAmountModifier(list) {
      // HACK CurrentCharge > nextBill.amount > getSelectedPlans, we have to exclude the delta part
      const planListGroupByCycle = _.chain(this.getSelectedPlans)
        .groupBy('billing_cycle')
        .value(); // this is edit delta flow
      return _.map(list, (x) => {
        if (_.has(planListGroupByCycle, x.billingCycle)) {
          const planList = _.get(planListGroupByCycle, [x.billingCycle]);
          const { boughtPlanAmount, boughtPlanDiscount } = _.reduce(planList, (pre, cartPlan) => {
            const boughtPlan = _.find(this.boughtPlans, (b) => b.plan_code === cartPlan.plan_code);
            pre.boughtPlanAmount += this.$refs.planDetail.mx_price(boughtPlan);
            pre.boughtPlanDiscount += this.$refs.planDetail.compute_delta_discount(boughtPlan)?.discount;
            return pre;
          }, { boughtPlanAmount: 0, boughtPlanDiscount: 0 });
          // boughtPlanAmount is bought plan's total standard amount = 3 plans standard amount 6000
          // boughtPlanDiscount is bought plan's total discount amount = 3 plans discount amount 3000 with 50% discount
          x.amount = boughtPlanAmount - boughtPlanDiscount;
        } else {
          x.amount = 0;
        }
        return x;
      });
    },
  },
  watch: {
    visible: {
      handler(val) {
        if (val) {
          this.$tracking.buyInteraction('product details', `${this.trackingName} current plan details - impression`);
        }
      },
    },
  },
};
</script>

<style lang="scss">
.opc-zod {
  font-family: Almaden Sans;

  &.opc-modal {
    .zm-dialog {
      margin: 0 !important;
      width: 85% !important;
      min-width: auto !important;
      height: auto;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }

  &__head {
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid #dfdfe4;
    padding-bottom: 8px;
    margin-bottom: 12px;

    h2 {
      font-size: 24px;
      font-weight: 600;
      line-height: 32px;
      text-align: left;
      color: #131619;
    }

    span {
      font-size: 14px;
      font-weight: 500;
      line-height: 32px;
      text-align: right;
      color: #6e7680;
    }
  }

  .opc-cart-item {
    margin-bottom: 12px;
  }

  &__footer {
    padding-top: 12px;
    border-top: 1px solid #dfdfe4;
  }
}
</style>
